// The 3D client's lobby: your games, a new game, and the games to join, over the table. Every call
// is the 2D client's API module; this page only renders and wires. A tab loads when first shown.
import {
  createGame,
  getAcceptedFriends,
  getFriendGames,
  getMyGameInvites,
  getPublicGames,
  getSavedGames,
  joinGame,
  joinGameById,
  loadSavedGame
} from '/js/api/brass-api.js';
import { getGameHistory, getProfile } from '/js/api/profile-api.js';
import { resolveGreetingName } from '/js/landing/greeting-name.js';
import { requireAuth } from '/shared/js/auth.js';
import { button, el, link } from '../hud/hud.js';
import { createBackdrop } from './backdrop.js';
import { createOptions, historyRow, joinableRow, lobbyHref, savedRow } from './games.js';
import { inviteKey } from './room-model.js';

const JOIN_REFRESH_MS = 5000;
const FINISHED_ROWS = 10;

function row(r, action) {
  const li = el('li', 'lobby-row');
  li.dataset.testid = r.testid;
  li.append(el('span', 'lobby-row-title', r.title), el('span', 'lobby-row-meta', r.meta), action);
  return li;
}

async function main() {
  const userInfo = await requireAuth();
  if (!userInfo) return;
  createBackdrop(document.getElementById('table'));
  const me = userInfo.userId;
  const note = document.getElementById('lobby-note');
  const byId = (id) => document.getElementById(id);

  // A call's failure lands on the page, where the person reads it; nothing is swallowed.
  function report(message) {
    note.textContent = message ?? '';
    note.hidden = !message;
  }

  // A call that ends in a room: on success the page leaves (true), on failure the note says why.
  async function go(label, call) {
    try {
      window.location.href = lobbyHref(await call());
      return true;
    } catch (err) {
      report(`${label}: ${err.message}`);
      return false;
    }
  }

  // A list is rebuilt only when its rows changed; the join lists refresh every few seconds.
  const rendered = new Map();
  function fill(id, rows, empty) {
    const key = JSON.stringify(rows.map((r) => [r.gameId, r.title, r.meta]));
    if (rendered.get(id) === key) return;
    rendered.set(id, key);
    byId(id).replaceChildren(...(rows.length ? rows.map((r) => row(r, r.action)) : [el('li', 'lobby-empty', empty)]));
  }

  getProfile()
    .catch(() => null)
    .then((profile) => {
      byId('lobby-greeting').textContent = `Welcome, ${resolveGreetingName(profile, userInfo)}.`;
    });

  // Your games: one history page, bucketed by the row's section; saved games apart.
  async function loadGames() {
    const [history, saved] = await Promise.all([getGameHistory({ status: 'all' }), getSavedGames()]);
    const rows = (history.items ?? []).map((g) => historyRow(g, me));
    fill(
      'games-open',
      rows
        .filter((r) => r.section === 'open')
        .map((r) => ({
          ...r,
          testid: 'game-row',
          action: link(r.action.label, r.action.href, { primary: true, testid: 'open-game' })
        })),
      'No game under way. Start one, or join one.'
    );
    fill(
      'games-saved',
      (saved.items ?? [])
        .map((s) => savedRow(s, me))
        .map((r) => ({
          ...r,
          testid: 'saved-row',
          action: button('Bring back', {
            testid: 'load-saved',
            onClick: () => go('Could not bring the game back', () => loadSavedGame(r.gameId).then(() => r.gameId))
          })
        })),
      'No saved game.'
    );
    fill(
      'games-finished',
      rows
        .filter((r) => r.section === 'finished')
        .slice(0, FINISHED_ROWS)
        .map((r) => ({ ...r, testid: 'finished-row', action: link(r.action.label, r.action.href) })),
      'No finished game yet.'
    );
  }

  // New game: the same options as the 2D form; the invite code is kept for the room.
  const form = byId('new-form');
  const friends = byId('new-friends');
  async function loadFriends() {
    const page = await getAcceptedFriends();
    friends.replaceChildren(
      ...(page.items ?? []).map((f) => {
        const option = el('option', null, f.displayName || f.friendUserId);
        option.value = f.friendUserId;
        return option;
      })
    );
    friends.disabled = friends.options.length === 0;
  }
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const submit = form.querySelector('[type=submit]');
    submit.disabled = true;
    const left = await go('Could not create the game', async () => {
      const created = await createGame(
        createOptions({
          visibility: data.get('visibility'),
          playerCount: data.get('playerCount'),
          timer: data.get('timer'),
          invitedUserIds: data.getAll('friends')
        })
      );
      sessionStorage.setItem(inviteKey(created.gameId), created.inviteCode);
      return created.gameId;
    });
    // The page is leaving on success; a button alive meanwhile would create a second room.
    if (!left) submit.disabled = false;
  });

  // Join: by invite code, or a seat at a game someone else hosts.
  const joinForm = byId('join-form');
  joinForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const code = new FormData(joinForm).get('code')?.toString().trim();
    if (!code) return;
    const submit = joinForm.querySelector('[type=submit]');
    submit.disabled = true;
    const left = await go('Could not join with that code', () => joinGame(code).then((joined) => joined.gameId));
    if (!left) submit.disabled = false;
  });
  async function loadJoinable() {
    const [invites, friendGames, publicGames] = await Promise.all([
      getMyGameInvites(),
      getFriendGames(),
      getPublicGames()
    ]);
    const lists = [
      ['join-invites', invites, 'No invitation waiting.'],
      ['join-friends', friendGames, "No friend's game open."],
      ['join-public', publicGames, 'No public game open.']
    ];
    for (const [id, page, empty] of lists) {
      fill(
        id,
        (page.items ?? []).map(joinableRow).map((r) => ({
          ...r,
          testid: 'joinable-row',
          action: button('Join', {
            primary: true,
            testid: 'join-game',
            onClick: () => go('Could not join', () => joinGameById(r.gameId).then(() => r.gameId))
          })
        })),
        empty
      );
    }
  }

  // Tabs: one section shown at a time, loaded on first show; the join lists keep refreshing
  // while that tab is in view and the page is visible.
  const LOADERS = { games: loadGames, new: loadFriends, join: loadJoinable };
  const tabs = [...document.querySelectorAll('[data-tab]')];
  const sections = [...document.querySelectorAll('[data-section]')];
  const loaded = new Set();
  let current = null;
  let joinTick = null;
  async function load(name) {
    if (loaded.has(name)) return;
    loaded.add(name);
    try {
      await LOADERS[name]();
      report();
    } catch (err) {
      loaded.delete(name);
      report(`Could not load the lobby: ${err.message}`);
    }
  }
  function scheduleJoin() {
    clearTimeout(joinTick);
    joinTick = setTimeout(async () => {
      if (current !== 'join') return;
      if (!document.hidden) await loadJoinable().catch(() => {});
      scheduleJoin();
    }, JOIN_REFRESH_MS);
  }
  function show(name) {
    current = name;
    for (const tab of tabs) tab.classList.toggle('hud-button--selected', tab.dataset.tab === name);
    for (const section of sections) section.hidden = section.dataset.section !== name;
    if (window.location.hash !== `#${name}`) history.replaceState(null, '', `#${name}`);
    load(name);
    if (name === 'join') scheduleJoin();
    else clearTimeout(joinTick);
  }
  for (const tab of tabs) tab.addEventListener('click', () => show(tab.dataset.tab));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && current === 'join') loadJoinable().catch(() => {});
  });
  const names = tabs.map((t) => t.dataset.tab);
  const start = window.location.hash.slice(1);
  show(names.includes(start) ? start : names[0]);
}

main().catch((err) => console.error('[brass3d] lobby', err));
